import React, { useMemo, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Spinner,
  Table,
} from 'reactstrap';
import { useMutation } from '@apollo/client/react';
import { useAuth } from '../../contexts/AuthContext';
import {
  UPDATE_ORDER_OPS_SERVICES,
  UpdateOrderOpsServicesData,
  UpdateOrderOpsServicesVariables,
} from '../../graphql/mutations/orders/updateOrderOpsServices';
import {
  formatOpsServiceDate,
  getArrivalDeparturePrefillFromLineItems,
  getOpsServiceStatusColor,
  getOpsServiceStatusLabel,
  getOpsServiceTypeLabel,
  getTourPrefillFromLineItems,
  NGK_OPS_SERVICES_META_KEY,
  parseOpsServicesFromMeta,
  removeOpsService,
  serializeOpsServices,
  upsertOpsService,
  type OpsService,
  type OpsServiceInput,
} from '../../utils/opsServices';
import type { SingleOrder } from '../../graphql/queries/orders/getOrder';
import OpsServiceFormModal from './OpsServiceFormModal';

interface BookingItineraryPanelProps {
  order: Pick<
    SingleOrder,
    'databaseId' | 'orderNumber' | 'metaData' | 'billing' | 'customer' | 'lineItems'
  >;
}

function getCustomerName(order: BookingItineraryPanelProps['order']): string {
  const billing = order.billing;
  if (billing?.firstName || billing?.lastName) {
    return [billing.firstName, billing.lastName].filter(Boolean).join(' ');
  }
  return order.customer?.email || billing?.email || '—';
}

const BookingItineraryPanel: React.FC<BookingItineraryPanelProps> = ({ order }) => {
  const { isCustomerPortalUser } = useAuth();
  const canEdit = !isCustomerPortalUser();
  const services = useMemo(
    () => parseOpsServicesFromMeta(order.metaData),
    [order.metaData]
  );

  const bookingNumber = `#${order.orderNumber ?? order.databaseId}`;
  const customerName = getCustomerName(order);
  const tourPrefill = useMemo(
    () => getTourPrefillFromLineItems(order.lineItems?.nodes),
    [order.lineItems]
  );
  const arrDepPrefill = useMemo(
    () => getArrivalDeparturePrefillFromLineItems(order.lineItems?.nodes, 'arrival'),
    [order.lineItems]
  );

  const hasTourOnBooking = !!tourPrefill.serviceName;
  const hasPackageOnBooking = useMemo(() => {
    const nodes = order.lineItems?.nodes || [];
    return nodes.some((item) => {
      const meta: Record<string, string> = {};
      (item.metaData || []).forEach((entry) => {
        if (entry?.key) meta[entry.key] = entry.value || '';
      });
      const name = (item.product?.node?.name || '').toLowerCase();
      const isTransfer =
        !!meta._tat_vehicle ||
        !!meta._tat_pickup ||
        !!meta._tat_pickup_name ||
        !!meta.tat_fields_flight_no ||
        !!meta._tat_extra_fields ||
        name.includes('transfer') ||
        name.includes('airport');
      const isHotelPackage =
        meta._ngk_hotel_booking === 'yes' ||
        !!meta._ngk_hotel_name ||
        !!meta.packageInfo;
      return isTransfer || isHotelPackage;
    });
  }, [order.lineItems]);

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<OpsService | null>(null);
  const [addPrefill, setAddPrefill] = useState<Partial<OpsServiceInput> | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [saveServices, { loading }] = useMutation<
    UpdateOrderOpsServicesData,
    UpdateOrderOpsServicesVariables
  >(UPDATE_ORDER_OPS_SERVICES, {
    refetchQueries: ['GetOrder', 'GetOrders', 'GetCustomerOrders'],
    awaitRefetchQueries: true,
  });

  const persist = async (next: OpsService[]) => {
    setError(null);
    try {
      await saveServices({
        variables: {
          orderId: order.databaseId,
          metaData: [
            {
              key: NGK_OPS_SERVICES_META_KEY,
              value: serializeOpsServices(next),
            },
          ],
        },
      });
    } catch (err: any) {
      const gqlMessage =
        err?.graphQLErrors?.[0]?.message ||
        err?.networkError?.result?.errors?.[0]?.message;
      setError(gqlMessage || err?.message || 'Unable to save services.');
      throw err;
    }
  };

  const openAddTour = () => {
    setEditing(null);
    setAddPrefill(tourPrefill.serviceName ? tourPrefill : { serviceType: 'tour', status: 'scheduled' });
    setModalOpen(true);
  };

  const openAddArrDep = () => {
    setEditing(null);
    setAddPrefill(
      Object.keys(arrDepPrefill).length
        ? arrDepPrefill
        : { serviceType: 'arrival', serviceName: 'Arrival', status: 'scheduled' }
    );
    setModalOpen(true);
  };

  const openEdit = (service: OpsService) => {
    setEditing(service);
    setAddPrefill(null);
    setModalOpen(true);
  };

  const onSave = async (input: OpsServiceInput) => {
    const next = upsertOpsService(services, input);
    await persist(next);
    setModalOpen(false);
    setEditing(null);
    setAddPrefill(null);
  };

  const onDelete = async (service: OpsService) => {
    const confirmed = window.confirm(`Remove service "${service.serviceName}"?`);
    if (!confirmed) return;
    await persist(removeOpsService(services, service.id));
  };

  if (!canEdit && services.length === 0) {
    return null;
  }

  return (
    <div className="mt-4">
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
        <div>
          <h6 className="mb-1">Itinerary / Services</h6>
          <p className="text-muted small mb-0">
            Manual daily-ops services for this booking (Arrival, Departure, Tour, Transfer).
          </p>
        </div>
        {canEdit ? (
          <div className="d-flex flex-wrap gap-2">
            {hasTourOnBooking ? (
              <Button color="primary" size="sm" onClick={openAddTour}>
                + Add tour service
              </Button>
            ) : null}
            {hasPackageOnBooking ? (
              <Button color="primary" size="sm" outline={hasTourOnBooking} onClick={openAddArrDep}>
                + Add arrival / departure
              </Button>
            ) : null}
            {!hasTourOnBooking && !hasPackageOnBooking ? (
              <Button color="primary" size="sm" onClick={openAddTour}>
                + Add service
              </Button>
            ) : null}
          </div>
        ) : null}
      </div>

      {error ? (
        <Alert color="danger" className="py-2">
          {error}
        </Alert>
      ) : null}

      {services.length === 0 ? (
        <Alert color="info" className="mb-0">
          No services yet. Add arrivals, departures, or tours for each operation date.
        </Alert>
      ) : (
        <div className="table-responsive">
          <Table className="table-bordered table-sm align-middle mb-0">
            <thead className="table-light">
              <tr>
                <th>Date</th>
                <th>Type</th>
                <th>Service</th>
                <th>Code</th>
                <th>Agency</th>
                <th>Nationality</th>
                <th>Hotel</th>
                <th>Pax</th>
                <th>INC</th>
                <th>OPT</th>
                <th>Flight</th>
                <th>From</th>
                <th>To</th>
                <th>Flight time</th>
                <th>Pickup</th>
                <th>Time</th>
                <th>Guide</th>
                <th>Car</th>
                <th>Driver</th>
                <th>Coord</th>
                <th>Status</th>
                {canEdit ? <th>Action</th> : null}
              </tr>
            </thead>
            <tbody>
              {services.map((service) => {
                const isTour = service.serviceType === 'tour';
                const isArrDep =
                  service.serviceType === 'arrival' || service.serviceType === 'departure';
                return (
                  <tr key={service.id}>
                    <td>{formatOpsServiceDate(service.serviceDate)}</td>
                    <td>
                      <Badge color="info">{getOpsServiceTypeLabel(service.serviceType)}</Badge>
                    </td>
                    <td className="fw-medium">{service.serviceName}</td>
                    <td>{service.fileNo || '—'}</td>
                    <td>{service.agency || '—'}</td>
                    <td>{service.nationality || '—'}</td>
                    <td>{isArrDep ? service.hotel || '—' : '—'}</td>
                    <td>{service.pax ?? '—'}</td>
                    <td>{isTour ? service.includedPax ?? '—' : '—'}</td>
                    <td>{isTour ? service.optionalPax ?? '—' : '—'}</td>
                    <td>{isArrDep ? service.flightNumber || '—' : '—'}</td>
                    <td>{isArrDep ? service.from || '—' : '—'}</td>
                    <td>{isArrDep ? service.to || '—' : '—'}</td>
                    <td>{isArrDep ? service.flightTime || '—' : '—'}</td>
                    <td>{isTour ? service.pickupTime || '—' : '—'}</td>
                    <td>{service.serviceTime || '—'}</td>
                    <td>{isTour ? service.guide || '—' : '—'}</td>
                    <td>{service.vehicle || '—'}</td>
                    <td>{service.driver || '—'}</td>
                    <td>{service.coordinator || '—'}</td>
                    <td>
                      <Badge color={getOpsServiceStatusColor(service.status)}>
                        {getOpsServiceStatusLabel(service.status)}
                      </Badge>
                    </td>
                    {canEdit ? (
                      <td>
                        <div className="d-flex gap-1">
                          <Button
                            color="primary"
                            size="sm"
                            outline
                            onClick={() => openEdit(service)}
                          >
                            Edit
                          </Button>
                          <Button
                            color="danger"
                            size="sm"
                            outline
                            onClick={() => onDelete(service)}
                            disabled={loading}
                          >
                            Remove
                          </Button>
                        </div>
                      </td>
                    ) : null}
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </div>
      )}

      {loading ? (
        <div className="mt-2">
          <Spinner size="sm" color="primary" /> <span className="text-muted small">Saving...</span>
        </div>
      ) : null}

      {canEdit ? (
        <OpsServiceFormModal
          isOpen={modalOpen}
          toggle={() => {
            setModalOpen(false);
            setAddPrefill(null);
          }}
          initial={editing}
          prefill={editing ? null : addPrefill}
          bookingNumber={bookingNumber}
          customerName={customerName}
          onSave={onSave}
          saving={loading}
        />
      ) : null}
    </div>
  );
};

export default BookingItineraryPanel;
